import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const RUNTIME_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Interactive map banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 5,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A map image for this property — site plan, community map, aerial site map, or similar map graphic when one exists in the gallery. Never use logos, brand marks, or unit floor plan drawings.',
    context: 'Full-bleed banner hero on the Interactive Apartment Map page. Prefer an actual map or community site-plan image over a building exterior or unit interior. Never use logos or unit floor plan layout drawings. Must be a different photo from every other page hero. Do not reuse other page hero images.',
  },
};

export function InteractiveMapHero() {
  const bgImage = useSlotGroup('interactivemaphero.bg_image', RUNTIME_DEF);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="interactivemaphero.bg_image" definition={RUNTIME_DEF} className="absolute inset-0">
        {bgImage.image ? (
          <VibesitesImage url={bgImage.image} alt={bgImage.alt || 'Interactive map banner'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/30" />
      </EditableSlotGroup>

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="interactivemaphero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteractiveMapHero',
  displayName: 'Interactive Map Hero',
  tokens: {
    'interactivemaphero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Interactive Map</p>',
    },
  },
  slotGroups: {
    'interactivemaphero.bg_image': RUNTIME_DEF,
  },
};

(InteractiveMapHero as any).contentSchema = contentSchema;

export default InteractiveMapHero;
